<script>
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';

  export let options = []; // Array of { value: any, label: string } or just values
  export let selectedValue;
  export let placeholder = 'Select...';
  export let label = ''; // Optional label above the select
  export let ariaLabel = 'Custom select';

  let isOpen = false;
  const dispatch = createEventDispatcher();
  let selectElement; // Reference to the main element

  // Normalize options to { value, label } format
  $: normalizedOptions = options.map(opt => 
    typeof opt === 'object' && opt !== null && 'value' in opt && 'label' in opt 
      ? opt 
      : { value: opt, label: String(opt) }
  );

  // Find the label for the currently selected value
  $: selectedLabel = normalizedOptions.find(opt => opt.value === selectedValue)?.label || placeholder;

  function toggleDropdown() {
    isOpen = !isOpen;
  }

  function selectOption(option) {
    if (selectedValue !== option.value) {
      selectedValue = option.value;
      dispatch('change', selectedValue); // Dispatch the new value
    }
    isOpen = false; // Close dropdown after selection
  }

  // Close dropdown if clicking outside
  function handleClickOutside(event) {
    if (selectElement && !selectElement.contains(event.target)) {
      isOpen = false;
    }
  }

  onMount(() => {
    document.addEventListener('click', handleClickOutside, true);
  });

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside, true);
  });
</script>

<div class="custom-select-container" bind:this={selectElement}>
  {#if label}
    <div class="select-label">{label}</div>
  {/if}
  <button 
    class="select-selected {isOpen ? 'open' : ''}" 
    on:click={toggleDropdown} 
    aria-haspopup="listbox" 
    aria-expanded={isOpen}
    aria-label={ariaLabel}
  >
    <span class="selected-text">{selectedLabel}</span>
    <span class="select-arrow">▼</span>
  </button>

  {#if isOpen}
    <div class="select-options" role="listbox">
      {#each normalizedOptions as option (option.value)}
        <button 
          class="select-option {selectedValue === option.value ? 'selected' : ''}" 
          on:click={() => selectOption(option)}
          role="option"
          aria-selected={selectedValue === option.value}
        >
          {option.label}
        </button>
      {:else}
        <div class="select-option disabled">No options</div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .custom-select-container {
    position: relative;
    display: inline-block; /* Or block, depending on layout needs */
    font-family: inherit; /* Inherit font from parent */
    width: 100%;
  }

  .select-label {
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
    opacity: 0.9;
    color: white; /* Assuming parent context has dark background */
    text-align: center;
    font-weight: 700;
  }

  .select-selected {
    background: linear-gradient(135deg, var(--box-title-color), #343a40); /* Match card */
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex; /* Use flexbox */
    justify-content: space-between; /* Space between text and arrow */
    align-items: center;
    width: 100%; /* Adjust as needed */
    font-size: 2rem; /* Match previous style */
    font-weight: 700; 
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: background-color 0.2s ease;
    position: relative; /* Needed for z-index if options overlap other elements */
    z-index: 2; 
    margin-bottom: 0px;
  }
  
  .selected-text {
     flex-grow: 1; /* Allow text to take available space */
     overflow: hidden;
     white-space: nowrap;
     text-overflow: ellipsis;
  }

  .select-selected:hover,
  .select-selected:focus {
    background: linear-gradient(135deg, #34495e, #404953); /* Slightly lighter on hover */
  }

  .select-arrow {
    font-size: 0.8rem;
    transition: transform 0.2s ease;
    pointer-events: none; /* Doesn't interfere with button click */
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
  }

  .select-selected.open .select-arrow {
    transform: translateY(-50%) rotate(180deg); /* Point arrow up when open */
  }

  .select-options {
    position: absolute;
    background-color: #343a40; /* Dark background for options */
    border-radius: 8px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    left: 0;
    right: 0;
    overflow-y: auto;
    z-index: 10; /* Ensure it's above other content */
    animation: fadeIn 0.2s ease-out;
  }

  .select-option {
    background-color: transparent;
    color: white;
    padding: 12px 16px;
    border: none;
    width: 100%;
    cursor: pointer;
    font-size: 1rem; /* Regular font size for options */
    display: block; /* Ensure it takes full width */
    transition: background-color 0.15s ease;
  }

  .select-option:hover,
  .select-option:focus {
    background-color: #495057; /* Highlight on hover/focus */
    outline: none;
  }

  .select-option.selected {
    background-color: var(--box-title-color); /* Different background for selected */
    font-weight: bold;
  }
  
  .select-option.disabled {
    color: #6c757d;
    cursor: default;
  }

  /* Simple fade-in animation */
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }

</style> 